import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Box, Typography } from "@mui/material";
import Sidebar from "./Navigation/Sidebar";
import Events from "./Events/Events";
import EventCenter from "./Events/EventCenter";
import { UserProvider, useUser } from "./UserContext";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Pitches from "./Pitches/PitchAdd";
import HomeRouter from "./Home/HomeRouter";
import RavenAI from "./Raven AI/RavenAI";
import Settings from "./Settings/Settings";
import MassBookings from "./Booking/MassBookings";
import AppBar from "./Navigation/AppBar";
import ProfileRouter from "./Profiles/ProfileRouter";
import Staff from "./Agency/Staff";
import BALeaderboard from "./Analytics/BA/BALeaderboard";
import AnalyticsRouter from "./Analytics/AnalyticsRouter";
import Live from "./Events/Live";
import { ProtectedRoute } from "./Components/ProtectedRoute";
import Login from "./Login/Login";
import axios from "axios";

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  user_access?: {
    tags?: string[];
    user_status?: {
      Verified?: string;
      Admin?: string;
      Active?: string;
    };
  };
}

// Add a placeholder ManageUsers component
const ManageUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const { currentUser } = useUser();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/users/users`
        );
        const activeUsers = response.data.filter(
          (user: User) => user.user_access?.user_status?.Active === "Yes"
        );
        setUsers(activeUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (currentUser?.role === "Developer") {
      fetchUsers();
    }
  }, [currentUser]);

  if (currentUser?.role !== "Developer") {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manage Users
      </Typography>
      <Box sx={{ mt: 2 }}>
        {users.map((user) => (
          <Box
            key={user.id}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              bgcolor: "background.paper",
              boxShadow: 1,
            }}
          >
            <Typography variant="h6">
              {user.first_name} {user.last_name}
            </Typography>
            <Typography color="text.secondary">{user.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              Role: {user.role}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const AppContent: React.FC = () => {
  const [_selectedAccountId, setselectedAccountId] = useState<number | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { logout } = useUser();
  const location = useLocation();

  const handleLogout = () => {
    logout();
  };

  const handleAccountSelect = (accountId: number) => {
    setselectedAccountId(accountId);
  };

  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev);
  };

  const isLoginPage = location.pathname === "/login";

  return (
    <>
      {!isLoginPage && (
        <AppBar isExpanded={isExpanded} toggleSidebar={toggleSidebar} />
      )}
      <Box sx={{ display: "flex", pt: isLoginPage ? 0 : 7 }}>
        {!isLoginPage && (
          <Sidebar
            onLogout={handleLogout}
            onaccountSelect={handleAccountSelect}
            isExpanded={isExpanded}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isLoginPage ? 0 : 3,
            width: isLoginPage ? "100%" : "auto",
          }}
        >
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomeRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mass-bookings"
              element={
                <ProtectedRoute>
                  <MassBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage-users"
              element={
                <ProtectedRoute>
                  <ManageUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ambassador-events"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-center"
              element={
                <ProtectedRoute>
                  <EventCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pitches"
              element={
                <ProtectedRoute>
                  <Pitches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <Staff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <BALeaderboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live"
              element={
                <ProtectedRoute>
                  <Live />
                </ProtectedRoute>
              }
            />
            <Route
              path="/RavenAI"
              element={
                <ProtectedRoute>
                  <RavenAI />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:username"
              element={
                <ProtectedRoute>
                  <ProfileRouter />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <UserProvider>
        <Router>
          <AppContent />
        </Router>
      </UserProvider>
    </ThemeProvider>
  );
};

export default App;
